import {ConnectingEditor, TextEditorServices} from '@frontapp/editor-html';
import {
  ContentEditable as DefaultContentEditable,
  ContentEditableProps as DefaultContentEditableProps,
} from '@frontapp/editor-html/lib/react';
import {noop} from 'lodash';
import React, {FC, RefCallback, useCallback, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {isInputLike} from '../../../../libs/shared/helpers/src/browser/domHelpers';
import {
  annotateTaskType,
  TaskTypesEnum,
} from '../../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {EventHandler, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {textSelection} from '../../../../libs/shared/ui/src/styles/mixins';
import {startSimplePerformanceInteraction} from '../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionTypesEnum} from '../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {GrammarlyEditorPluginWrapper} from '../../grammarly/grammarlyEditorPluginWrapper';
import {useVariableHeightElement} from '../scroll/useVariableHeightElement';

/*
 * Constants.
 */

export enum EditorTypesEnum {
  COMMENT = 'comment',
  TWEET = 'tweet',
  EMAIL = 'email',
  CHAT = 'chat',
}

/*
 * Props.
 */

export interface ContentEditableProps extends DefaultContentEditableProps {
  // We can only accept a callback-style ref, since we need multiple refs to the same element.
  refCallback?: RefCallback<HTMLDivElement | null>;
  className?: string;
  editor: ConnectingEditor<HTMLDivElement, TextEditorServices>;
  shouldFocus?: boolean;
  isDisabled?: boolean;
  hasGrammarlySupport?: boolean;
  onPaste?: Handler;
  onHeightChange?: HandlerOf<number>;
  editorType?: EditorTypesEnum;
}

/*
 * Style.
 */

const StyledDefaultContentEditable = styled(DefaultContentEditable)`
  ${textSelection()};

  *,
  *::after,
  *::before {
    box-sizing: initial;
  }

  a,
  a:visited,
  a:active {
    color: ${(p) => p.theme.interactiveText};
  }

  a:hover {
    color: ${(p) => p.theme.interactiveTextHover};
  }

  .front-blockquote {
    margin: 0;
    border-left: 1px solid ${(p) => p.theme.palette.grey.shade30};
    padding-left: 10px;
  }
`;

const StyledDiv = styled.div`
  *,
  *::after,
  *::before {
    box-sizing: initial;
  }
`;

/*
 * Component.
 */

export const ContentEditable: FC<ContentEditableProps> = ({refCallback, editor, shouldFocus, ...props}) => {
  const contentEditableRef = useRef<HTMLDivElement | null>(null);

  // Callback-style ref that acts as a multiplexer for our own ref, the editor ref, and the ref callback from props.
  const contentEditableRefCallback = useCallback<RefCallback<HTMLDivElement>>(
    (element) => {
      contentEditableRef.current = element;
      editor.targetRef(element);
      if (refCallback) {
        refCallback(element);
      }
    },
    [refCallback, editor],
  );

  // Report changes in our height every time we re-render.
  const measureElementHeight = useVariableHeightElement(contentEditableRef, undefined, props.onHeightChange);

  useEffect(() => {
    // Note: the text service will report changes when an HTML editor changes also.
    const subscription = editor.services.text.changes$.subscribe(() => {
      // Also report changes in our height when our DOM content changes.
      measureElementHeight();
    });

    return () => subscription.unsubscribe();
  }, [editor, measureElementHeight]);

  // Handle shouldFocus prop.
  useInitialEffect(() => {
    if (!shouldFocus) {
      return () => {};
    }

    // TEMP: remove when we refactored the message composer to have unsaved drafts in its own state.
    // If the focus already is inside an input-like element, don't steal the focus.
    if (
      contentEditableRef.current &&
      contentEditableRef.current.ownerDocument &&
      isInputLike(contentEditableRef.current.ownerDocument.activeElement)
    ) {
      return () => {};
    }

    editor.services.focus.focus();
    // TODO: Figure out why this is necessary. Without this, cursor starts at the end of the first at-mention
    // decoration in a tweet reply, even if there are multiple at-mentions.
    editor.services.selection.moveToEnd();
    return () => {};
  });

  // Handle paste events
  useInitialEffect(() => {
    const pasteHandler = () => {
      props.onPaste?.();
    };
    contentEditableRef.current?.addEventListener('paste', pasteHandler);
    return () => {
      contentEditableRef.current?.removeEventListener('paste', pasteHandler);
    };
  });

  // Measure typing interaction performance.
  useInitialEffect(() => {
    const contentEditable = contentEditableRef.current;
    if (!contentEditable) {
      // Do nothing.
      return noop;
    }

    const listener: EventHandler = (event) => {
      annotateTaskType(TaskTypesEnum.KEYPRESS_EVENT);
      startSimplePerformanceInteraction(event, PerformanceInteractionTypesEnum.TEXT_INPUT);
    };
    contentEditable.addEventListener('keypress', listener);
    return () => contentEditable.removeEventListener('keypress', listener);
  });

  if (props.isDisabled) {
    return renderDisabledContentEditable(props.className);
  }

  return (
    <GrammarlyEditorPluginWrapper
      hasGrammarlySupport={props.hasGrammarlySupport}
      isComment={props.editorType === EditorTypesEnum.COMMENT}
      isReply={
        props.editorType === EditorTypesEnum.EMAIL ||
        props.editorType === EditorTypesEnum.CHAT ||
        props.editorType === EditorTypesEnum.TWEET
      }
    >
      <StyledDefaultContentEditable {...props} ref={contentEditableRefCallback} />
    </GrammarlyEditorPluginWrapper>
  );
};

function renderDisabledContentEditable(className: string | undefined) {
  return (
    <StyledDiv className={className} role="textbox" aria-disabled>
      <div>
        <br />
      </div>
    </StyledDiv>
  );
}
